<template>
  <v-card>
    <v-card-title>
      <span v-if="order.id">発注編集</span>
      <span v-else>発注作成</span>
      <v-btn
        v-if="order.status == 'writing'"
        class="ml-5 font-weight-bold"
        dark
        color="danger"
        depressed
        small
        @click="cancelOrder"
      >
        初期化する
      </v-btn>
      <v-spacer />

      <v-spacer />
      <v-btn icon @click="cancelEdit">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text height="500">
      <v-form ref="orderForm" v-model="valid" lazy-validation>
        <v-row>
          <v-col md="5">
            <v-text-field
              v-model="orderData.query"
              :rules="[presenceValidation]"
              required
            >
              <template v-slot:label>
                <InputLabel label-name="クエリ" :required="true" />
              </template>
            </v-text-field>
          </v-col>
          <v-col md="5">
            <v-text-field
              v-model="orderData.requiredWords"
              class="mb-3"
              :rules="[presenceValidation]"
              type="number"
            >
              <template v-slot:label>
                <InputLabel label-name="規定文字数" :required="true" />
              </template>
            </v-text-field>
          </v-col>
          <v-col md="12">
            <v-text-field
              v-model="orderData.charUnitPrice"
              type="number"
              :rules="[inRangeValidation]"
              step="0.01"
              min="0"
            >
              <template v-slot:label>
                <InputLabel
                  label-name="文字単価(小数第二位まで入力して下さい。小数第三位は四捨五入されます。)"
                />
              </template>
            </v-text-field>
          </v-col>
          <v-col md="5">
            <v-text-field v-model="orderData.bonus" class="mb-3" type="number">
              <template v-slot:label>
                <InputLabel label-name="ボーナス" />
              </template>
            </v-text-field>
          </v-col>
          <v-col md="5">
            <v-text-field
              v-model="orderData.salesPrice"
              class="mb-3"
              type="number"
            >
              <template v-slot:label>
                <InputLabel label-name="販売単価(税抜)" />
              </template>
            </v-text-field>
          </v-col>
          <v-col md="12">
            <v-text-field
              v-model="orderData.title"
              :rules="[presenceValidation]"
              required
            >
              <template v-slot:label>
                <InputLabel label-name="発注タイトル" :required="true" />
              </template>
            </v-text-field>
          </v-col>
          <v-col md="12">
            <v-text-field v-model="orderData.postClassification">
              <template v-slot:label>
                <InputLabel label-name="記事種別" :required="false" />
              </template>
            </v-text-field>
          </v-col>
          <v-col md="7">
            <v-select
              v-model="orderData.userId"
              :items="writers"
              :item-text="item => writerName(item)"
              item-value="id"
            >
              <template v-slot:label>
                <InputLabel label-name="ライター" />
              </template>
            </v-select>
          </v-col>
          <v-col md="7">
            <v-select
              v-model="orderData.regulationBundlerId"
              :items="regulationBundlers"
              item-text="name"
              item-value="id"
            >
              <template v-slot:label>
                <InputLabel label-name="レギュレーション" />
              </template>
            </v-select>
          </v-col>
          <v-col md="12">
            <div class="mb-1">
              <InputLabel label-name="締め切り" />
            </div>
            <datepicker
              v-model="orderData.expiredOn"
              placeholder="締め切りを選択"
              format="yyyy年 M月 d日"
            />
          </v-col>
          <v-col md="12">
            <v-text-field v-model="orderData.lotName">
              <template v-slot:label>
                <InputLabel label-name="ロット名" :required="false" />
              </template>
            </v-text-field>
          </v-col>
          <v-col md="12">
            <v-row>
              <v-col md="3">
                <v-text-field
                  v-model="orderData.postId"
                  class="mb-3"
                  type="number"
                >
                  <template v-slot:label>
                    <InputLabel label-name="記事ID" />
                  </template>
                </v-text-field>
              </v-col>
              <v-col md="9">
                <v-alert dense outlined type="error" class="text-caption">
                  ※1. 新規発注の場合、記事IDは入力しないでください。<br />
                  ※2.
                  既存の記事を発注に紐づける場合のみ、記事IDを入力して下さい。
                </v-alert>
              </v-col>
            </v-row>
          </v-col>
          <v-col md="12">
            <InputLabel label-name="ランクキーワード" />
            <v-card
              v-for="(keywords, rankName) in orderData.rankKeywords"
              :key="`${orderData.id}-${rankName}`"
              elevation="0"
              tile
              outlined
              class="mt-2"
            >
              <v-card-title class="text-subtitle-2 pa-3 pb-2">
                {{ rankName.toUpperCase() }} ランク
              </v-card-title>
              <v-card-text class="pa-3 pt-0">
                <RankKeywordsInput
                  v-if="keywords"
                  :keywords="keywords"
                  @change-keywords="changeKeywords($event, rankName)"
                />
              </v-card-text>
            </v-card>
          </v-col>
          <v-col md="12">
            <v-textarea
              v-model="orderData.instruction"
              class="body-2"
              label="仕様書"
            >
              <template v-slot:label>
                <InputLabel label-name="仕様書" />
              </template>
            </v-textarea>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-row>
        <v-col md="10" offset-md="1" sm="12">
          <v-btn
            v-if="order.id"
            color="primary"
            depressed
            block
            @click="updateOrder"
          >
            更新する
          </v-btn>
          <v-btn v-else color="success" depressed block @click="createOrder">
            作成する
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
import Datepicker from 'vuejs-datepicker'
import RankKeywordsInput from '@/components/commons/orders/RankKeywordsInput'
import InputLabel from '@/components/commons/formParts/inputLabel'
import formValidationUtils from '@/mixins/form_validation_utils'

export default {
  components: { Datepicker, RankKeywordsInput, InputLabel },
  props: {
    order: {
      type: Object,
      required: true
    },
    writers: {
      type: Array,
      required: true
    },
    regulationBundlers: {
      type: Array,
      required: true
    }
  },
  mixins: [formValidationUtils],
  data () {
    return {
      valid: true,
      inRangeValidation: function (value) {
        if (!value) {
          return true
        } else {
          return !!(value > 0 && value < 100) || '100未満の数を入力して下さい'
        }
      },
      testKeywords: [{ key: 1, value: 'aaaa' }]
    }
  },
  computed: {
    orderData: {
      get () {
        return this.order
      },
      set (value) {
        this.$emit('change-order', value)
      }
    }
  },
  methods: {
    validateForm () {
      this.valid = this.$refs.orderForm.validate()
    },
    updateOrder () {
      this.validateForm()
      if (!this.valid) {
        return false
      }

      this.$emit('update-order', this.order)
      this.valid = true
    },
    createOrder () {
      this.validateForm()
      if (!this.valid) {
        return false
      }

      this.$emit('create-order', this.order)
      this.valid = true
    },
    cancelOrder () {
      if (
        window.confirm(
          '初期化を行うと、現在執筆しているライターの登録が解除され、発注作成時の状態となり、執筆中の記事の内容も初期化されます。\n 本当に初期化しますか？'
        )
      ) {
        this.$emit('cancel-order', this.order)
      }

      return false
    },
    cancelEdit () {
      this.$refs.orderForm.reset()
      this.$emit('cancel-edit')
    },
    changeKeywords (keywords, rankName) {
      this.orderData.rankKeywords[rankName] = keywords
    },
    writerName (writer) {
      if (writer.name) {
        return writer.name
      } else if (writer.userAuth.email) {
        return `名前未設定（${writer.userAuth.email}）`
      } else {
        return '名前未設定（メールアドレス非公開）'
      }
    }
  }
}
</script>

<style lang="sass">
.vdp-datepicker input
  border-radius: 5px
  padding: 7px 10px
  border: 1px solid #707070
  width: 300px
</style>
